import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ChevronLeft, 
  Inbox, 
  FileText, 
  Download, 
  CheckCircle2, 
  UserPlus, 
  Save, 
  AlertCircle,
  Clock,
  Sparkles
} from 'lucide-react';
import { ENQUIRY_STATUS_CONFIG, EnquiryStatus } from '@ftt/shared';
import { Breadcrumb } from '../../components/common/Breadcrumb';

export const AdminEnquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [conversionSuccess, setConversionSuccess] = useState<any>(null);

  const { data: enquiry, isLoading, error } = useQuery({
    queryKey: ['admin-enquiry-detail', id],
    queryFn: () => api.get(`/enquiries/${id}`).then((res) => {
      const data = res.data.data;
      setStatus(data.status);
      setAdminNotes(data.adminNotes || '');
      return data;
    }),
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      return api.patch(`/enquiries/${id}/status`, { status, adminNotes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enquiry-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-enquiries'] });
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    },
  });

  const convertMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/enquiries/${id}/convert`);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-enquiry-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-enquiries'] });
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      setConversionSuccess(res.data.data);
    },
  });

  if (isLoading) {
    return <div className="p-12 text-center text-xs font-mono text-slate-400">Loading submission details...</div>;
  }

  if (error || !enquiry) {
    return (
      <div className="p-12 text-center space-y-4">
        <h2 className="text-xl font-serif font-bold text-white">Enquiry Not Found</h2>
        <Link to="/admin/enquiries" className="text-xs font-bold text-brand-400 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to enquiries
        </Link>
      </div>
    );
  }

  const files = (enquiry.files as any[]) || [];

  return (
    <div className="space-y-6 max-w-5xl">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          theme="dark"
          items={[
            { label: 'Admin', href: '/admin' },
            { label: 'Enquiries', href: '/admin/enquiries' },
            { label: enquiry.referenceNo },
          ]}
        />
      </div>

      {/* Conversion Success Notification */}
      {conversionSuccess && (
        <div className="p-6 rounded-2xl bg-emerald-950/80 border border-emerald-500 text-emerald-200 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            <h3 className="font-serif text-lg font-bold text-white">Enquiry Successfully Converted!</h3>
          </div>
          <p className="text-xs leading-relaxed">
            A new client profile has been provisioned and publishing project <strong>{conversionSuccess.project?.projectCode}</strong> has been initialized with all 12 milestones and transferred manuscript files.
          </p>
          {conversionSuccess.tempPassword && (
            <div className="p-3 bg-slate-900/90 rounded-lg font-mono text-xs text-amber-300 border border-amber-800/60">
              <span>New Client Account Initial Password: <strong>{conversionSuccess.tempPassword}</strong></span>
            </div>
          )}
          <div className="pt-2 flex gap-3 text-xs">
            <Link
              to={`/admin/projects`}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold"
            >
              View In Project Manager &rarr;
            </Link>
          </div>
        </div>
      )}

      {/* Header Card */}
      <div className="p-8 rounded-2xl bg-slate-950 border border-slate-800 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-bold text-brand-400 px-2.5 py-0.5 rounded bg-slate-900 border border-slate-800">
                {enquiry.referenceNo}
              </span>
              <span className="text-xs text-slate-400 font-mono">
                {enquiry.genre}
              </span>
            </div>
            <h1 className="font-serif text-3xl font-bold text-white">
              {enquiry.bookTitle}
            </h1>
            <p className="text-xs text-slate-400">
              Submitted by <strong>{enquiry.fullName}</strong> ({enquiry.email}) on {new Date(enquiry.createdAt).toLocaleDateString()}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {enquiry.status !== 'CONVERTED' ? (
              <button
                onClick={() => convertMutation.mutate()}
                disabled={convertMutation.isPending}
                className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold text-xs flex items-center gap-2 shadow-sm transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                <span>Convert to Client &amp; Project</span>
              </button>
            ) : (
              <span className="px-4 py-2 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-300 font-mono text-xs font-bold">
                ✓ Converted to Active Project
              </span>
            )}
          </div>
        </div>

        {/* Specification Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-850">
            <span className="text-slate-500 block text-[10px] uppercase">Author / Organization</span>
            <span className="font-bold text-white">{enquiry.authorName}</span>
            <span className="text-slate-400 block text-[11px]">{enquiry.organization || 'Independent'} &bull; {enquiry.country}</span>
          </div>

          <div className="p-4 rounded-xl bg-slate-900 border border-slate-850">
            <span className="text-slate-500 block text-[10px] uppercase">Current Stage &amp; Words</span>
            <span className="font-bold text-white">{enquiry.stage.replace('_', ' ')}</span>
            <span className="text-slate-400 block text-[11px]">{enquiry.wordCount ? `${enquiry.wordCount.toLocaleString()} words` : 'Word count not specified'}</span>
          </div>

          <div className="p-4 rounded-xl bg-slate-900 border border-slate-850">
            <span className="text-slate-500 block text-[10px] uppercase">Target Timeline</span>
            <span className="font-bold text-white">{enquiry.expectedTimeline || 'Standard timeline'}</span>
          </div>
        </div>

        {/* Requirements */}
        <div className="space-y-2 border-t border-slate-800/80 pt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 font-mono block">
            Requested Publishing Requirements:
          </span>
          <div className="flex flex-wrap gap-2">
            {(enquiry.requirements as string[]).map((r, idx) => (
              <span key={idx} className="px-3 py-1 rounded-md bg-slate-900 border border-slate-800 text-xs text-brand-300 font-medium">
                {r}
              </span>
            ))}
          </div>
        </div>

        {/* Additional Notes */}
        {enquiry.additionalInfo && (
          <div className="space-y-1 text-xs text-slate-300 border-t border-slate-800/80 pt-4 font-sans">
            <span className="font-bold uppercase text-[10px] font-mono text-slate-500 block">Author Notes / Synopsis</span>
            <p className="leading-relaxed">{enquiry.additionalInfo}</p>
          </div>
        )}
      </div>

      {/* Submitted Files */}
      <div className="p-8 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
        <h3 className="font-serif text-lg font-bold text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-brand-400" />
          <span>Attached Manuscript &amp; Proposal Files ({files.length})</span>
        </h3>

        {files.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">No files were attached with this submission.</div>
        ) : (
          <div className="space-y-2">
            {files.map((file, idx) => (
              <div
                key={idx}
                className="p-3.5 rounded-lg bg-slate-900 border border-slate-850 flex items-center justify-between text-xs text-slate-300"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-brand-400 shrink-0" />
                  <span className="font-bold text-white">{file.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">({Math.round(file.size / 1024)} KB)</span>
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono text-xs flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status & Internal Administrative Notes */}
      <div className="p-8 rounded-2xl bg-slate-950 border border-slate-800 space-y-6">
        <h3 className="font-serif text-lg font-bold text-white border-b border-slate-800 pb-3">
          Acquisitions Status &amp; Committee Notes
        </h3>

        {updateSuccess && (
          <div className="p-3 rounded-lg bg-emerald-950 border border-emerald-500 text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Enquiry status updated successfully.</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
              Acquisitions Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden focus:border-brand-500"
            >
              <option value="NEW">New</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="CONTACTED">Contacted</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="PROPOSAL_SENT">Proposal Sent</option>
              <option value="CONVERTED">Converted to Client</option>
              <option value="DECLINED">Declined</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>

        <div className="text-xs">
          <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
            Internal Committee Notes (Private to Staff)
          </label>
          <textarea
            rows={4}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Enter private internal evaluation notes, reviewer feedback, or scheduling details..."
            className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden focus:border-brand-500"
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => updateStatusMutation.mutate()}
            disabled={updateStatusMutation.isPending}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>Save Status &amp; Notes</span>
          </button>
        </div>
      </div>

    </div>
  );
};
